import type { AxiosInstance } from 'axios';
import { createAxiosClient } from '@billda/shared-ui';
import { authAdapter, handleAuthFailure } from './authAdapter';
import { baseUrl, clientType, appVersion } from './apiClientConfig';

/**
 * Handle network errors during token refresh.
 * Dispatches a global event for the ConnectionErrorDialog to handle.
 * User stays logged in.
 */
function handleNetworkError(
  message: string,
  retryQueue: () => Promise<void>,
  isColdStart: boolean
): void {
  console.error('🚨 Network error during token refresh:', message);

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('auth:network-error', {
        detail: {
          message,
          retryQueue,
          isColdStart,
        },
      })
    );
  }
}

/**
 * Create axios clients using the shared factory.
 */
const { authenticatedInstance, publicInstance, handleTokenRefresh } =
  createAxiosClient({
    baseURL: baseUrl,
    authStore: authAdapter,
    onAuthFailure: handleAuthFailure,
    onNetworkError: handleNetworkError,
    testMode: import.meta.env.TEST === 'true',
    forceInterceptors: false,
    clientType,
    appVersion,
  });

// Export axios instances for general use
export const apiClient: AxiosInstance = authenticatedInstance;
export const publicApiClient: AxiosInstance = publicInstance;

// Export a wrapper object for backward compatibility with navigation injection
export const axiosClient = {
  getInstance: () => authenticatedInstance,
  createPublicInstance: () => publicInstance,
};

// Named export for testing
export { authenticatedInstance as AxiosClient };

/**
 * Shared token refresh function with deduplication.
 * Use this from auth stores instead of making direct API calls
 * to avoid concurrent refresh race conditions.
 */
export { handleTokenRefresh as sharedTokenRefresh };
